import { PrimaryButtonLabel } from '../../../types/enums';
import { HandleStartAndEndSelectionProps, KeyEvent, WeekSpanProps } from './../types/propTypes';
import styles from './Calendar.module.scss';

const daysInARow = 7;
const hour12system = 12;
const monthsInAYear = 12;
const daysInAMonth = 30;
const milliseconds = 900000;

export const roundUpValue = (valueTo: Date | undefined) =>
  valueTo ? new Date(Math.round(valueTo.getTime() / milliseconds) * milliseconds) : new Date(0);

export const rowsOfDates = (showFullCalendar: boolean, noOfDays: number, firstDay: number) =>
  showFullCalendar ? Math.ceil((noOfDays + firstDay) / daysInARow) : 5;

export const shouldSkipSelection = (e: KeyEvent, isRange: boolean): boolean =>
  e.type === 'focus' || (e.type === 'mouseover' && !isRange);

export const getScheduledDateTime = (roundedInitialDate: Date | undefined, is24HoursFormat: boolean) => {
  if (roundedInitialDate) {
    return {
      year: roundedInitialDate.getFullYear(),
      month: roundedInitialDate.getMonth(),
      date: roundedInitialDate.getDate(),
      time: roundedInitialDate.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: !is24HoursFormat,
      }),
    };
  }
  return roundedInitialDate;
};

export const getScheduledDateTimeTo = (ValueTo: Date | undefined, roundedEndDate: Date, is24HoursFormat: boolean) => {
  if (ValueTo) {
    return {
      year: roundedEndDate.getFullYear(),
      month: roundedEndDate.getMonth(),
      date: roundedEndDate.getDate(),
      time: roundedEndDate.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: !is24HoursFormat,
      }),
    };
  }
  return {
    year: 0,
    month: 0,
    date: 0,
    time: '',
  };
};

export const handleDateChange = (
  prev: Date | undefined,
  date: Date,
  setPrimaryButtonLabel: (value: React.SetStateAction<PrimaryButtonLabel>) => void,
) => {
  if (prev && prev?.getTime() !== date?.getTime()) {
    setPrimaryButtonLabel(PrimaryButtonLabel.APPLY);
  }
  return date;
};

export const handleTimeChange = (
  prev: string,
  time: string,
  setPrimaryButtonLabel: (value: React.SetStateAction<PrimaryButtonLabel>) => void,
) => {
  if (prev !== time) {
    setPrimaryButtonLabel(PrimaryButtonLabel.APPLY);
  }
  return time;
};

export const splitArrayInGroups = (arr: React.ReactElement[], size: number) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push({ id: i, name: arr.slice(i, i + size) });
  }
  return result;
};

export const createSelectDate = (
  displayDate: { date: number; month: number } | false,
  isDisabled: boolean,
  year: number,
  selectedDateTime: Date,
): Date | undefined => {
  if (displayDate && !isDisabled) {
    return new Date(
      year,
      displayDate.month,
      displayDate.date,
      selectedDateTime.getHours(),
      selectedDateTime.getMinutes(),
    );
  }
  return undefined;
};

export const isMouseoverInRange = (
  e: KeyEvent,
  selectDate: Date,
  selectedDateTimeEnd: Date,
  selectedDateTime: Date,
  minValue: Date,
) =>
  e.type === 'mouseover' &&
  selectedDateTimeEnd.getTime() === minValue.getTime() &&
  selectDate.getTime() > selectedDateTime.getTime();

export const handleStartAndEndSelection = ({
  selectDate,
  selectedDateTime,
  selectedDateTimeEnd,
  setSelectedDateTime,
  setSelectedDateTimeEnd,
  setSelectedDateTimeEndHover,
  minValue,
  e,
  onChange,
}: HandleStartAndEndSelectionProps) => {
  if (isMouseoverInRange(e, selectDate, selectedDateTimeEnd, selectedDateTime, minValue)) {
    setSelectedDateTimeEndHover(selectDate);
  } else if (e.type === 'mouseover' && selectedDateTimeEnd.getTime() !== minValue.getTime()) {
    return;
  } else if (selectDate.getTime() < selectedDateTime.getTime()) {
    setSelectedDateTime(selectDate);
    setSelectedDateTimeEnd(minValue);
    setSelectedDateTimeEndHover(minValue);
  } else if (selectedDateTimeEnd.getTime() === minValue.getTime()) {
    setSelectedDateTimeEnd(selectDate);
    onChange?.(selectedDateTime, selectDate, e);
  } else {
    setSelectedDateTime(selectDate);
    setSelectedDateTimeEnd(minValue);
    setSelectedDateTimeEndHover(minValue);
  }
};

export const createSelectedTime = (
  time: string,
  isEnd: boolean,
  selectedDateTime: Date,
  selectedDateTimeEnd: Date,
): Date => {
  const { hours, minutes } = parseTimeString(time);

  const selectedTime = new Date(isEnd ? selectedDateTimeEnd : selectedDateTime);

  selectedTime.setHours(hours, minutes);

  return selectedTime;
};

export const getTimeList = (intervalInMins = 15, hours12 = true, startingHour = 0) => {
  const timeList = [];
  const date = new Date();

  for (let hour = 0; hour < 24; hour++) {
    for (let interval = 0; interval < 60 / intervalInMins; interval++) {
      timeList.push([(hour + startingHour) % 24, interval * 15]);
    }
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: hours12,
  });

  return timeList.map((time) => {
    const [hour, minute] = time;
    date.setHours(hour);
    date.setMinutes(minute);
    const formattedTime = formatter.format(date);
    return { id: formattedTime, name: formattedTime };
  });
};

export const parseTimeString = (time: string): { hours: number; minutes: number } => {
  const [hour, rest] = time.split(':');
  const [minute, ampm] = rest.split(' ');

  let hours = Number(hour);
  if (ampm === 'PM' && hours !== hour12system) {
    hours += hour12system;
  } else if (ampm === 'AM' && hours === hour12system) {
    hours = 0;
  }

  return { hours, minutes: Number(minute) };
};

export const isSelectedDate = (
  displayDate: { date: number; month: number } | false,
  selectedDateTime: Date,
  year: number,
  month: number,
): { date: number; month: number } | false => {
  if (displayDate) {
    const isSelected =
      new Date(selectedDateTime.getFullYear(), selectedDateTime.getMonth(), selectedDateTime.getDate()).getTime() ===
      new Date(year, month, displayDate.date).getTime();

    return isSelected ? displayDate : false;
  }

  return false;
};

export const isSelectedToDate = (
  displayDate: { date: number; month: number } | false,
  selectedDateTimeEnd: Date,
  selectedDateTimeEndHover: Date,
  selectRange: boolean,
  year: number,
  month: number,
): boolean =>
  displayDate &&
  selectRange &&
  (new Date(
    selectedDateTimeEnd.getFullYear(),
    selectedDateTimeEnd.getMonth(),
    selectedDateTimeEnd.getDate(),
  ).getTime() === new Date(year, month, displayDate.date).getTime() ||
    new Date(
      selectedDateTimeEndHover.getFullYear(),
      selectedDateTimeEndHover.getMonth(),
      selectedDateTimeEndHover.getDate(),
    ).getTime() === new Date(year, month, displayDate.date).getTime());

export const isSelectedRangeDate = (
  displayDate: { date: number; month: number } | false,
  selectedDateTime: Date,
  selectedDateTimeEnd: Date,
  selectedDateTimeEndHover: Date,
  year: number,
  month: number,
): boolean =>
  displayDate &&
  new Date(selectedDateTime.getFullYear(), selectedDateTime.getMonth(), selectedDateTime.getDate()).getTime() <
    new Date(year, month, displayDate.date).getTime() &&
  (new Date(
    selectedDateTimeEnd.getFullYear(),
    selectedDateTimeEnd.getMonth(),
    selectedDateTimeEnd.getDate(),
  ).getTime() > new Date(year, month, displayDate.date).getTime() ||
    new Date(
      selectedDateTimeEndHover.getFullYear(),
      selectedDateTimeEndHover.getMonth(),
      selectedDateTimeEndHover.getDate(),
    ).getTime() > new Date(year, month, displayDate.date).getTime());

export const isDisabledDate = (
  displayDate: { date: number; month: number } | false,
  disabledDates: Date[] | undefined,
  disabledPastDates: boolean | undefined,
  year: number,
  month: number,
  disabledFutureDates?: boolean,
  disabledPastNDates?: number,
): boolean => {
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  const isPastDateDisabled = () => {
    if (disabledPastDates && displayDate) {
      const compareDate = new Date(year, month, displayDate.date);
      return compareDate.getTime() < todayDate.getTime();
    }
    return false;
  };

  const isFutureDateDisabled = () => {
    if (disabledFutureDates && displayDate) {
      const compareDate = new Date(year, month, displayDate.date);
      return compareDate.getTime() > todayDate.getTime();
    }
    return false;
  };

  const isPastNDateDisabled = () => {
    if (disabledPastNDates && displayDate) {
      const compareDate = new Date(year, month, displayDate.date);
      return compareDate.getTime() < todayDate.getTime() - disabledPastNDates * 24 * 60 * 60 * 1000;
    }
    return false;
  };

  return !!(
    (displayDate &&
      disabledDates?.find(
        (disabledDate) =>
          disabledDate.getTime() === new Date(year, month, displayDate ? displayDate.date : undefined).getTime(),
      )) ||
    isPastDateDisabled() ||
    isFutureDateDisabled() || 
    isPastNDateDisabled()
  );
};

export const calculateDisplayDate = (
  showFullCalendar: boolean,
  show4WeeksOnly: boolean,
  date: Date,
  DAY_LIMIT: number,
  i: number,
  j: number,
) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month).getDay();
  const noOfDays = new Date(year, month + 1, 0).getDate();
  const today = date.getDate();
  const currentDate = i * daysInARow + j + (showFullCalendar ? 1 - firstDay : today);
  let displayDate = currentDate > 0 &&
    currentDate <= (show4WeeksOnly ? DAY_LIMIT : noOfDays) && {
      date: currentDate,
      month,
    };

  if (!showFullCalendar) {
    if (i * daysInARow + j > daysInAMonth) {
      displayDate = false;
    } else if (currentDate > noOfDays) {
      displayDate = { date: currentDate - noOfDays, month: month + 1 };
    } else {
      displayDate = { date: currentDate, month };
    }
  }

  return displayDate;
};

export const createWeekSpan: React.FC<WeekSpanProps> = ({
  displayDate,
  isSelected,
  isSelectedFrom,
  isSelectedTo,
  isSelectedRange,
  isDisabled,
  j,
  i,
  onClickHandler,
  onKeyDownHandler,
  onMouseOverHandler,
  onFocusHandler,
  ariaLabel,
}: WeekSpanProps) => (
  <span
    className={`${styles['date-cell']} ${displayDate && styles['selectable-cell']} ${
      isSelected || isSelectedFrom || isSelectedTo ? styles['selected-cell'] : ''
    } ${isDisabled ? styles['disabled-cell'] : ''}
      ${isSelectedRange ? styles['selected-range-cell'] : ''}
      ${isSelectedRange && j === 0 ? styles['range-left'] : ''}
      ${isSelectedRange && j === 6 ? styles['range-right'] : ''}`}
    onClick={onClickHandler}
    onKeyDown={onKeyDownHandler}
    onMouseOver={onMouseOverHandler}
    onFocus={onFocusHandler}
    key={`${i}${j}${displayDate ? displayDate.date : ''}`}
    tabIndex={0}
    role="button"
    aria-label={ariaLabel}
    data-testid="week-span-testid"
  >
    {displayDate && displayDate.date}
  </span>
);

type WeekType = React.ReactNode[];
export const createDateRow = (selectRange: boolean, week: WeekType, i: number) => (
  <div className={` ${selectRange ? styles['date-row-range'] : ''} ${styles['date-row']}`} key={`week-${i + 1}`}>
    {selectRange}
    {week}
  </div>
);
type WeekItem = string | number | boolean | JSX.Element | Iterable<React.ReactNode> | null | undefined;
type GetWeekFunction = (i: number, month: number) => WeekItem[];
export const getDates = (
  getWeek: GetWeekFunction,
  showFullCalendar: boolean,
  selectRange: boolean,
  rows: number,
  month: number,
) => {
  const dates = [];
  for (let i = 0; i < rows; i++) {
    const week = getWeek(i, month);

    if (!showFullCalendar && i) {
      dates.push(<div className={styles['divider']} />);
    }
    dates.push(createDateRow(selectRange, week, i));
  }
  return dates;
};

export const newYearDirection = (direction: 'left' | 'right', year: number, month: number) =>
  direction === 'left' ? year - (month === 0 ? 1 : 0) : year + (month === 11 ? 1 : 0);

export const newMonthDirection = (direction: 'left' | 'right', month: number) =>
  (month + (direction === 'left' ? -1 : 1) + monthsInAYear) % monthsInAYear;

export const calendarTitleStyling = (calendarTitle: string) =>
  calendarTitle ? styles['show-calendar-title-only'] : '';

export const selectedRangeStyling = (selectRange: boolean) => (selectRange ? styles['date-row-range'] : '');

export const selectedRangeLabel = (selectRange: boolean, timeLabelStart: string, timeLabel: string) =>
  selectRange ? timeLabelStart : timeLabel;
